/**
 * BulkImportDialog - CSV import dialog
 *
 * Allows importing students from a CSV file.
 * Expected format: name,sheet_number (sheet_number optional)
 */

import { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Upload, FileText, AlertCircle, CheckCircle2 } from "lucide-react";

// ============================================================================
// Constants
// ============================================================================

const MAX_CSV_SIZE = 1024 * 1024; // 1MB max

// ============================================================================
// Types
// ============================================================================

interface ParsedStudent {
  name: string;
  sheetNumber?: string;
  error?: string;
}

interface BulkImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (students: Array<{ name: string; sheetNumber?: string }>) => void;
  existingSheetNumbers?: string[];
}

// ============================================================================
// CSV Parsing and Sanitization
// ============================================================================

/**
 * Sanitize cell value to prevent CSV formula injection
 * Strips leading =, +, -, @ characters which could be interpreted as formulas
 */
function sanitizeCell(value: string): string {
  return value.replace(/^[=+\-@]+/, '');
}

function parseCSV(text: string, existingSheetNumbers: string[] = []): ParsedStudent[] {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  const students: ParsedStudent[] = [];
  const seenSheetNumbers = new Set<string>();
  const existingSet = new Set(existingSheetNumbers);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip header row if detected
    if (i === 0 && /^name/i.test(line)) {
      continue;
    }

    // Parse CSV line (handle quoted values)
    const parts = line.match(/("([^"]*)")|([^,]+)/g) || [];
    const values = parts.map((p) => p.replace(/^"|"$/g, "").trim());

    // Sanitize both name and sheetNumber to prevent formula injection
    const name = sanitizeCell(values[0] || "");
    const sheetNumber = values[1] ? sanitizeCell(values[1]) : undefined;

    if (!name) {
      students.push({ name: "", error: "Name is required" });
      continue;
    }

    // Validate sheet number if provided
    if (sheetNumber) {
      const num = parseInt(sheetNumber);
      if (isNaN(num) || num < 1 || num > 999) {
        students.push({
          name,
          sheetNumber,
          error: "Sheet number must be 1-999",
        });
        continue;
      }

      // Check for duplicates within this import file
      if (seenSheetNumbers.has(sheetNumber)) {
        students.push({
          name,
          sheetNumber,
          error: `Duplicate sheet number in file: ${sheetNumber}`,
        });
        continue;
      }

      // Check for duplicates against existing students in the class
      if (existingSet.has(sheetNumber)) {
        students.push({
          name,
          sheetNumber,
          error: `Sheet number ${sheetNumber} already exists in class`,
        });
        continue;
      }

      seenSheetNumbers.add(sheetNumber);
    }

    students.push({ name, sheetNumber: sheetNumber || undefined });
  }

  return students;
}

// ============================================================================
// Component
// ============================================================================

export function BulkImportDialog({
  open,
  onOpenChange,
  onImport,
  existingSheetNumbers = [],
}: BulkImportDialogProps) {
  const [parsedStudents, setParsedStudents] = useState<ParsedStudent[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validStudents = parsedStudents.filter((s) => !s.error && s.name);
  const invalidStudents = parsedStudents.filter((s) => s.error);

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file size
      if (file.size > MAX_CSV_SIZE) {
        setError(`File too large (${(file.size / 1024).toFixed(0)}KB). Maximum size: 1MB`);
        setParsedStudents([]);
        event.target.value = "";
        return;
      }

      setFileName(file.name);
      setError(null);

      try {
        const text = await file.text();
        const students = parseCSV(text, existingSheetNumbers);

        if (students.length === 0) {
          setError("No valid students found in file");
          setParsedStudents([]);
          return;
        }

        setParsedStudents(students);
      } catch (err) {
        setError("Failed to read file");
        setParsedStudents([]);
      }

      // Reset input so same file can be selected again
      event.target.value = "";
    },
    [existingSheetNumbers]
  );

  const handleImport = () => {
    onImport(validStudents.map((s) => ({ name: s.name, sheetNumber: s.sheetNumber })));
    onOpenChange(false);
    setParsedStudents([]);
    setFileName(null);
  };

  const handleClose = () => {
    onOpenChange(false);
    setParsedStudents([]);
    setFileName(null);
    setError(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Import Students from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file with student names. Optional second column for sheet numbers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File upload */}
          <div className="border-2 border-dashed rounded-lg p-6 text-center">
            <input
              type="file"
              accept=".csv,.txt"
              onChange={handleFileSelect}
              className="hidden"
              id="csv-upload"
            />
            <label
              htmlFor="csv-upload"
              className="cursor-pointer flex flex-col items-center gap-2"
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">
                {fileName || "Click to select CSV file"}
              </span>
              <span className="text-xs text-muted-foreground">
                Format: name, sheet_number (one per line)
              </span>
            </label>
          </div>

          {/* Error */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Preview */}
          {parsedStudents.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Preview</span>
                <span className="text-sm text-muted-foreground">
                  {validStudents.length} valid
                  {invalidStudents.length > 0 && (
                    <span className="text-destructive">
                      , {invalidStudents.length} with errors
                    </span>
                  )}
                </span>
              </div>

              <ScrollArea className="h-[200px] border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="w-24">Sheet #</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedStudents.map((student, index) => (
                      <TableRow
                        key={index}
                        className={student.error ? "bg-destructive/10" : ""}
                      >
                        <TableCell>
                          {student.error ? (
                            <AlertCircle className="h-4 w-4 text-destructive" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          )}
                        </TableCell>
                        <TableCell>
                          {student.name || (
                            <span className="text-destructive italic">
                              {student.error}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono">
                          {student.sheetNumber || (
                            <span className="text-muted-foreground">Auto</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}

          {/* Example format */}
          {parsedStudents.length === 0 && !error && (
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4" />
                <span className="text-sm font-medium">Example CSV format:</span>
              </div>
              <pre className="text-xs text-muted-foreground font-mono">
                {`name,sheet_number
Alice Johnson,1
Bob Smith,2
Carol Williams
David Brown,4`}
              </pre>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={validStudents.length === 0}
          >
            Import {validStudents.length} Student{validStudents.length !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
